import { WebContainer } from '@webcontainer/api';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { CLIENT_ORIGIN, WEBCONTAINER_ORIGIN } from '~/lib/constants';
import { menuStore } from '~/lib/stores/menu';
import { getToken } from '~/lib/stores/token';
import { userStore } from '~/lib/stores/user';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import { WORK_DIR_NAME } from '~/utils/constants';
import { getCoepHeader } from '~/utils/features';
import { getMemory, isOutOfMemoryError } from '~/utils/memory';
import { watchAll } from '~/utils/nanostore';

interface WebContainerContext {
  loaded: boolean;
}

export const webcontainerContext: WebContainerContext = import.meta.hot?.data.webcontainerContext ?? {
  loaded: false,
};

if (import.meta.hot) {
  import.meta.hot.data.webcontainerContext = webcontainerContext;
}

export let webcontainer: Promise<WebContainer> = new Promise(() => {
  // noop for ssr
});

Object.assign(globalThis, { WEBCONTAINER_API_IFRAME_URL: WEBCONTAINER_ORIGIN });

let timeout: ReturnType<typeof setTimeout> | undefined;

if (!import.meta.env.SSR) {
  const coep = getCoepHeader(navigator.userAgent);

  webcontainer =
    import.meta.hot?.data.webcontainer ??
    Promise.race([
      userStore.initialized.then((user) => {
        return WebContainer.boot({
          workdirName: WORK_DIR_NAME,
          forwardPreviewErrors: true,
          coep,
          experimentalNode: user?.featureFlags?.experimentalNode ?? false,
        });
      }),
      new Promise<never>((_, reject) => {
        timeout = setTimeout(() => reject(new WebContainerTimeoutError()), 30_000);
      }),
    ]);

  if (import.meta.hot) {
    import.meta.hot.data.webcontainer = webcontainer;
  }
}

webcontainer.then(
  (webcontainer) => {
    clearTimeout(timeout);

    if (import.meta.env.MODE !== 'production') {
      Object.assign(globalThis, { webcontainer });
    }

    webcontainerContext.loaded = true;

    watchAll(userStore.loggedIn, userStore.getActiveOrganization(), userStore.user).subscribe(
      ([loggedIn, organization, user]) => {
        if (loggedIn && organization && user?.featureFlags?.privateRegistries) {
          getToken().then((token) => {
            webcontainer.internal.setRegistryCredentials({
              accessToken: token,
              editorOrigin: CLIENT_ORIGIN,
              owner: {
                id: organization.slug,
                provider: organization.provider,
              },
            });
          });
        }
      },
    );

    webcontainer.on('error', (error) => {
      if (isOutOfMemoryError(error, false)) {
        menuStore.showFatalErrorDialog('out-of-memory', error instanceof Error ? error : new Error(error.message));
      } else {
        sendAnalyticsEvent({
          action: AnalyticsAction.Track,
          payload: {
            event: AnalyticsTrackEvent.WebContainerStartupError,
            properties: {
              ...getMemory(),
              error: error.message,
              stack: 'stack' in error ? error.stack : undefined,
            },
          },
        });
      }
    });
  },
  (error) => {
    const outOfMemory = isOutOfMemoryError(error, true);

    menuStore.showFatalErrorDialog(
      outOfMemory
        ? 'out-of-memory'
        : error instanceof WebContainerTimeoutError
          ? 'webcontainer-startup-timeout'
          : 'webcontainer-startup-error',
      error,
    );
  },
);

class WebContainerTimeoutError extends Error {
  name = 'WebContainerTimeoutError';

  constructor() {
    super('WebContainer took longer than 30s to boot');
  }
}
